import type { ValueOf } from "../../../pojo/Util";
import { Report as Reports } from "../../../pojo/Report";
import type { DocumentLayoutOptions } from "../../../services/document.service";

export namespace Doc {
    export type Type = ValueOf<typeof type>;
    export type TypeId = Type["id"];
    export type TypeReportId = Type["reportId"];
    export type Scope = "subreport" | "related";
    export type Mode = "create" | "copy" | "view" | "edit";

    export const DEFAULT_MARGINS = "0.25in 0.5in 0.17in 0.5in";
    export const DEFAULT_FONT_SIZE = 0.9;
    export const DEFAULT_ORIENTATION: DocumentLayoutOptions["page_orientation"] = "portrait";

    export const type = {
        event: {
            id: "event",
            label: "Event",
            order: 0,
            reportId: Reports.Reports.EventShell.id,
            interface: "standard",
            category: "Object Based",
            scopes: {
                standard: true,
                subreport: false,
            },
        },
        reservation: {
            id: "reservation",
            label: "Occurrence",
            order: 1,
            reportId: Reports.Reports.ReservationShell.id,
            interface: "standard",
            category: "Object Based",
            scopes: {
                standard: true,
                subreport: false,
            },
        },
        reservations: {
            id: "reservations",
            label: "Occurrences",
            order: 1.1,
            reportId: Reports.Reports.ReservationsShell.id,
            interface: "reportBuilder",
            category: "Search Based",
            scopes: {
                standard: true,
                subreport: true,
            },
        },
        organization: {
            id: "organization",
            label: "Organization",
            order: 2,
            reportId: Reports.Reports.OrganizationShell.id,
            interface: "standard",
            category: "Object Based",
            scopes: {
                standard: true,
                subreport: false,
            },
        },
        invoice: {
            id: "invoice",
            label: "Invoice (Payment)",
            order: 2.1,
            reportId: Reports.Reports.PaymentShell.id,
            interface: "standard",
            category: "Object Based",
            scopes: {
                standard: true,
                subreport: false,
            },
        },
        eventListing: {
            id: "eventListing",
            label: "Event Listing",
            order: 3,
            reportId: Reports.Reports.EventListingShell.id,
            interface: "tableBuilder",
            tableBuilder: [{ id: "data", label: "Data", key: "eventListing", variable: "ws.flexTable.events" }],
            category: "Excel",
            scopes: {
                standard: true,
                subreport: false,
            },
        },
        rsrvListing: {
            id: "rsrvListing",
            label: "Occurrences By Date",
            order: 4,
            reportId: Reports.Reports.ReservationListingShell.id,
            interface: "tableBuilder",
            tableBuilder: [
                { id: "data", label: "Data", key: "reservationListing", variable: "ws.flexTable.rsrvsByDate" },
            ],
            category: "Excel",
            scopes: {
                standard: true,
                subreport: false,
            },
        },
        locListing: {
            id: "locListing",
            label: "Location Listing",
            order: 5,
            reportId: Reports.Reports.LocationListingShell.id,
            interface: "tableBuilder",
            tableBuilder: [{ id: "data", label: "Data", key: "locationListing", variable: "ws.flexTable.locations" }],
            category: "Excel",
            scopes: {
                standard: true,
                subreport: false,
            },
        },
        resListing: {
            id: "resListing",
            label: "Resource Listing",
            order: 6,
            reportId: Reports.Reports.ResourceListingShell.id,
            interface: "tableBuilder",
            tableBuilder: [{ id: "data", label: "Data", key: "resourceListing", variable: "ws.flexTable.resources" }],
            category: "Excel",
            scopes: {
                standard: true,
                subreport: false,
            },
        },
        orgListing: {
            id: "orgListing",
            label: "Organization Listing",
            order: 7,
            reportId: Reports.Reports.OrganizationListingShell.id,
            interface: "tableBuilder",
            tableBuilder: [
                { id: "data", label: "Data", key: "organizationListing", variable: "ws.flexTable.organizations" },
            ],
            category: "Excel",
            scopes: {
                standard: true,
                subreport: false,
            },
        },
        invoiceExcel: {
            id: "invoiceExcel",
            label: "Invoice Details (Payment)",
            order: 8,
            reportId: Reports.Reports.InvoiceShell.id,
            interface: "tableBuilder",
            tableBuilder: [
                {
                    id: "summary",
                    label: "Summary",
                    key: "invoicePaymentSummary",
                    variable: "ws.flexTable.invoicePaymentSummary",
                },
                {
                    id: "details",
                    label: "Details",
                    key: "invoicePaymentDetails",
                    variable: "ws.flexTable.invoicePaymentDetails",
                },
            ],
            category: "Excel",
            scopes: {
                standard: true,
                subreport: false,
            },
        },
        rsrvReq: {
            id: "rsrvReq",
            label: "Requirements",
            order: 1,
            interface: "reportBuilder",
            reportId: null as number,
            scopes: {
                standard: false,
                subreport: true,
            },
        },
        rsrvEventCustAtrb: {
            id: "rsrvEventCustAtrb",
            label: "Custom Attributes",
            order: 2,
            interface: "reportBuilder",
            reportId: null as number,
            scopes: {
                standard: false,
                subreport: true,
            },
        },
        rsrvLoc: {
            id: "rsrvLoc",
            label: "Locations",
            order: 3,
            interface: "reportBuilder",
            reportId: null as number,
            scopes: {
                standard: false,
                subreport: true,
            },
        },
        rsrvRsrc: {
            id: "rsrvRsrc",
            label: "Resources",
            order: 4,
            interface: "reportBuilder",
            reportId: null as number,
            scopes: {
                standard: false,
                subreport: true,
            },
        },
        rsrvRsrcByCat: {
            id: "rsrvRsrcByCat",
            label: "Resources By Category",
            order: 5,
            interface: "reportBuilder",
            reportId: null as number,
            scopes: {
                standard: false,
                subreport: true,
            },
        },
        rsrvOrg: {
            id: "rsrvOrg",
            label: "Organizations",
            order: 6,
            interface: "reportBuilder",
            reportId: null as number,
            scopes: {
                standard: false,
                subreport: true,
            },
        },
        rsrvContact: {
            id: "rsrvContact",
            label: "Contacts",
            order: 7,
            interface: "reportBuilder",
            reportId: null as number,
            scopes: {
                standard: false,
                subreport: true,
            },
        },
        rsrvRsrcPref: {
            id: "rsrvRsrcPref",
            label: "Resource Preferences",
            order: 8,
            interface: "reportBuilder",
            reportId: null as number,
            scopes: {
                standard: false,
                subreport: true,
            },
        },
        rsrvLocPref: {
            id: "rsrvLocPref",
            label: "Location Preferences",
            order: 9,
            interface: "reportBuilder",
            reportId: null as number,
            scopes: {
                standard: false,
                subreport: true,
            },
        },
        segments: {
            id: "segments",
            label: "Segments",
            order: 10,
            interface: "reportBuilder",
            reportId: null as number,
            scopes: {
                standard: false,
                subreport: true,
            },
        },
    } as const;

    export const allTypes = Object.values(type).sort((a, b) => a.order - b.order);
    export const defaultTypes = allTypes.filter((type) => type.scopes.standard);
    export const subreportTypes = allTypes.filter((type) => type.scopes.subreport);

    export type TypeOptions = ValueOf<typeof type>[];
    export type ReportBuilderTypeId = ValueOf<{
        [P in keyof typeof type]: (typeof type)[P] extends { interface: "reportBuilder" } ? P : never;
    }>;
    export type SubreportTypeId = ValueOf<{
        [P in keyof typeof type]: (typeof type)[P] extends { scope: { subreport: true } } ? P : never;
    }>;

    export const typeById = new Map(Object.values(type).map((type) => [type.id, type]));
    export const typeByReportId = new Map(Object.values(type).map((type) => [type.reportId, type]));

    // Parses the columns of a table builder table variable
    // Format: {{for: <table variable> : <column1> || <column2> || ... :end-for}}
    // Columns: <variable> | <format> : <header> : <width>
    // Where only variable and header are required
    export const tableBuilderColumnRegex =
        /(?<variable>[^:|]+)\s*(?:\|\s*(?<format>[^:|]+)\s*)?:\s*(?<header>[^:|]+)\s*(?::\s*(?<width>\d+)\s*)?\s*(?:\|\||:end-for)/gi;

    export const tableBuilderRegex = /(?<table>\{\{for:\s*(?<variable>[$.\w\d]+):\s*(?<columns>.*?)\s*:end-for}})/gi;

    // Parses the columns of a complex table builder table variable
    // Format: {{for: <table variable> : <column1> || <column2> || ... :end-for}}
    // Columns: <report section> ^ <row number> ^ <label> ^ <variable> | <format> ^ <style> ^ <width>
    export const reportBuilderColumnRegex =
        /(?<section>[\w_]+)\s*\^\s*(?<row>\d+)\s*\^\s*(?<label>[^\^]+?)?\s*\^\s*(?<variable>[^\^]+?)?\s*\^\s*(?<style>[^\^]+?)?\s*\^\s*(?<width>[\d.]+)\s*(?:\|\||:end-for)/gi;

    export const supportedCssProperties = [
        "background",
        "background-attachment",
        "background-color",
        "background-image",
        "background-position",
        "background-repeat",
        "border",
        "border-bottom",
        "border-bottom-color",
        "border-bottom-style",
        "border-bottom-width",
        "border-collapse",
        "border-color",
        "border-left",
        "border-left-color",
        "border-left-style",
        "border-left-width",
        "border-right",
        "border-right-color",
        "border-right-style",
        "border-right-width",
        "border-spacing",
        "border-style",
        "border-top",
        "border-top-color",
        "border-top-style",
        "border-top-width",
        "border-width",
        "bottom",
        "caption-side",
        "clear",
        "clip",
        "color",
        "content",
        "counter-increment",
        "counter-reset",
        "direction",
        "display",
        "empty-cells",
        "float",
        "fonts",
        "font-family",
        "font-size",
        "font-size-adjust",
        "font-stretch",
        "font-style",
        "font-variant",
        "font-weight",
        "height",
        "left",
        "letter-spacing",
        "line-height",
        "list-style",
        "list-style-position",
        "list-style-type",
        "margin",
        "margin-bottom",
        "margin-left",
        "margin-right",
        "margin-top",
        "marker-offset",
        "max-height",
        "max-width",
        "min-height",
        "min-width",
        "orphans",
        "overflow",
        "padding",
        "padding-bottom",
        "padding-left",
        "padding-right",
        "padding-top",
        "page",
        "page-break-after",
        "page-break-before",
        "page-break-inside",
        "position",
        "quotes",
        "right",
        "size",
        "table-layout",
        "text-align",
        "text-decoration",
        "text-indent",
        "text-transform",
        "top",
        "unicode-bibi",
        "vertical-align",
        "visibility",
        "white-space",
        "widows",
        "width",
        "word-spacing",
        "z-index",
    ];

    export const reportBuilderDefaultSort = {
        reservations: ["reservation_start_dt", "reservation_end_dt", "event_name"],
        rsrvReq: ["requirement_name", "requirement_type_name", "requirement_count"],
        rsrvEventCustAtrb: ["attribute_name", "attribute_type_name", "attribute_value"],
        rsrvLoc: ["space_name", "max_capacity", "none"],
        rsrvRsrc: ["resource_name", "resource_count", "none"],
        rsrvRsrcByCat: ["resource_name", "resource_count", "none"],
        rsrvOrg: ["organization_name", "organization_type_name", "none"],
        rsrvContact: ["role_name", "contact_name", "none"],
        rsrvRsrcPref: ["pref_resource_name", "pref_resource_quantity", "none"],
        rsrvLocPref: ["pref_space_name", "pref_max_capacity", "none"],
        segments: ["init_start_dt", "init_end_dt", "none"],
        rsrvListing: ["reservation_start_dt", "reservation_end_dt", "event_start_dt"],
    };

    export const pageOrientationOptions = [
        { val: "portrait", txt: "Portrait" },
        { val: "landscape", txt: "Landscape" },
        { val: "8.5in 14in", txt: "8.5in x 14in" },
        { val: "14in 8.5in", txt: "14in x 8.5in" },
        { val: "11in 17in", txt: "11in x 17in" },
        { val: "17in 11in", txt: "17in x 11in" },
    ];
    export const pageOrientation = Object.fromEntries(pageOrientationOptions.map((option) => [option.val, option.txt]));

    export const reportBuilderGroupByOptions = [
        { val: "none", txt: "None" },
        { val: "date", txt: "By Date" },
        { val: "dateReservation", txt: "By Date & Occurrence" },
        { val: "weeklyCalendar", txt: "Weekly Calendar" },
        { val: "monthlyCalendar", txt: "Monthly Calendar" },
        { val: "monthlyCalendarContinuous", txt: "Monthly Calendar Continuous" },
    ];
    export const reportBuilderGroupBy = Object.fromEntries(
        reportBuilderGroupByOptions.map((option) => [option.val, option.txt]),
    );

    export const reportBuilderPageBreakOptions = [
        { val: "none", txt: "None" },
        { val: "group", txt: "By Group" },
        { val: "reservation", txt: "By Occurrence" },
    ];
    export const reportBuilderPageBreak = Object.fromEntries(
        reportBuilderPageBreakOptions.map((option) => [option.val, option.txt]),
    );

    export type ReportBuilderFunction =
        | "concat"
        | "dateCompare"
        | "dateTimeCompare"
        | "numberCompare"
        | "stringCompare"
        | "isNull"
        | "contdCalendarTimes"
        | "combinePeriodTimes";
    export type ReportBuilderFunctionArgument = {
        type: ReportBuilderFunctionArgumentType;
        variableTypes?: string[];
        options?: (Omit<ReportBuilderFunctionArgument, "options"> & { default?: boolean })[];
        label?: string;
        noFormat?: boolean;
        optional?: boolean;
    };
    export type ReportBuilderFunctionVariableArgument = ReportBuilderFunctionArgument & { buttonLabel: string };
    export type ReportBuilderFunctionArgumentType =
        | "text"
        | "variable"
        | "comparisonOperator"
        | "multi"
        | "date"
        | "dateTime"
        | "number";
    export type ReportBuilderFunctionDefinition = {
        info?: string;
        fixedArguments?: ReportBuilderFunctionArgument[];
        variableArguments?: ReportBuilderFunctionVariableArgument[];
    };

    export const reportBuilderFunctions: Record<string, ReportBuilderFunctionDefinition> = {
        concat: {
            info: "Combine variables and text in a single cell",
            variableArguments: [
                { type: "text", buttonLabel: "Add Text" },
                { type: "variable", buttonLabel: "Add Variable" },
            ],
        },
        dateCompare: {
            fixedArguments: [
                {
                    type: "multi",
                    label: "Date 1",
                    options: [
                        { type: "date" },
                        { type: "variable", variableTypes: ["date"], noFormat: true, default: true },
                    ],
                },
                {
                    type: "multi",
                    label: "Date 2",
                    options: [
                        { type: "date" },
                        { type: "variable", variableTypes: ["date"], noFormat: true, default: true },
                    ],
                },
                { type: "comparisonOperator", label: "Operator" },
                {
                    type: "multi",
                    label: "Value If True",
                    options: [{ type: "text" }, { type: "variable" }],
                    optional: true,
                },
                {
                    type: "multi",
                    label: "Value If False",
                    options: [{ type: "text" }, { type: "variable" }],
                    optional: true,
                },
            ],
        },
        dateTimeCompare: {
            fixedArguments: [
                {
                    type: "multi",
                    label: "Date-Time 1",
                    options: [
                        { type: "dateTime" },
                        { type: "variable", variableTypes: ["date"], noFormat: true, default: true },
                    ],
                },
                {
                    type: "multi",
                    label: "Date-Time 2",
                    options: [
                        { type: "dateTime" },
                        { type: "variable", variableTypes: ["date"], noFormat: true, default: true },
                    ],
                },
                { type: "comparisonOperator", label: "Operator" },
                {
                    type: "multi",
                    label: "Value If True",
                    options: [{ type: "text" }, { type: "variable" }],
                    optional: true,
                },
                {
                    type: "multi",
                    label: "Value If False",
                    options: [{ type: "text" }, { type: "variable" }],
                    optional: true,
                },
            ],
        },
        numberCompare: {
            fixedArguments: [
                {
                    type: "multi",
                    label: "Number 1",
                    options: [{ type: "number" }, { type: "variable", variableTypes: ["number"], default: true }],
                },
                {
                    type: "multi",
                    label: "Number 2",
                    options: [{ type: "number" }, { type: "variable", variableTypes: ["number"], default: true }],
                },
                { type: "comparisonOperator", label: "Operator" },
                {
                    type: "multi",
                    label: "Value If True",
                    options: [{ type: "text" }, { type: "variable" }],
                    optional: true,
                },
                {
                    type: "multi",
                    label: "Value If False",
                    options: [{ type: "text" }, { type: "variable" }],
                    optional: true,
                },
            ],
        },
        stringCompare: {
            fixedArguments: [
                {
                    type: "multi",
                    label: "Text 1",
                    options: [{ type: "text" }, { type: "variable", variableTypes: ["string"], default: true }],
                },
                {
                    type: "multi",
                    label: "Text 2",
                    options: [{ type: "text" }, { type: "variable", variableTypes: ["string"], default: true }],
                },
                { type: "comparisonOperator", label: "Operator" },
                {
                    type: "multi",
                    label: "Value If True",
                    options: [{ type: "text" }, { type: "variable" }],
                    optional: true,
                },
                {
                    type: "multi",
                    label: "Value If False",
                    options: [{ type: "text" }, { type: "variable" }],
                    optional: true,
                },
            ],
        },
        isNull: {
            fixedArguments: [
                { type: "variable", label: "Variable", noFormat: true },
                {
                    type: "multi",
                    label: "Value If True",
                    options: [{ type: "text" }, { type: "variable" }],
                    optional: true,
                },
                {
                    type: "multi",
                    label: "Value If False",
                    options: [{ type: "text" }, { type: "variable" }],
                    optional: true,
                },
            ],
        },
        contdCalendarTimes: {
            info: 'Smart date range display where "contd" is shown if range exceeds the span of the calendar',
            fixedArguments: [
                { type: "variable", variableTypes: ["date"], label: "Date 1", noFormat: true },
                { type: "variable", variableTypes: ["date"], label: "Date 2", noFormat: true },
            ],
        },
        combinePeriodTimes: {
            info: `Flexible date range display with dates shown as needed and an optional message if start and end is the same`,
            fixedArguments: [
                { type: "text", label: "Message If Undefined", optional: true },
                { type: "variable", variableTypes: ["date"], label: "Date 1" },
                { type: "variable", variableTypes: ["date"], label: "Date 2" },
                { type: "variable", variableTypes: ["date"], label: "Compare Date", noFormat: true },
            ],
        },
    };

    export const operatorMap = {
        eq: "=",
        ne: "≠",
        lt: "<",
        le: "<=",
        gt: ">",
        ge: ">=",
    } as const;

    export const parameterNameMap: Record<string, string> = {
        "Event ID": "Event Reference",
        "RSRV ID": "Occurrence",
        "Bill ID": "Invoice",
        "Org ID": "Organization",
    };
}
